import { components as c } from "@/components";
import { enabledEventTypes, useMyAccessModes } from "@/features/clinical-access/api/selectMyAccessModes";
import {
    searchForTemplateAiWizardNavigation,
    useTemplateAiWizardSearch,
} from "@/features/ai-builder/useTemplateAiDraftStore";
import { EVENT_TYPES_WITH_SKIP, skipInterventionOption } from "@/features/events/definitions/event-types";
import { LockableField, TemplateFieldLockLabelRow } from "@/features/templates/components/TemplateScopeLock";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { FormFieldLockedRegion } from "@patient-watch/ui/form";
import {
    Questionnaire,
    QuestionnaireChoice,
    QuestionnaireChoices,
    QuestionnaireItem,
} from "@patient-watch/ui/questionnaire";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useFinalizeTemplateWizard } from "@/features/templates/hooks/useFinalizeTemplateWizard";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { templateSettingsSchema, TrackingType, type TemplateSettingsSchemaT } from "@patient-watch/types";
import { z } from "zod";
import { useAuth } from "@/utils/auth";
import { isUuid } from "@/services/isUuid";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useMemo } from "react";

function stopSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    event.stopPropagation();
}

const EventsForm = () => {
    const auth = useAuth();
    const copy = useTemplatesCopy();
    const { data: accessModes } = useMyAccessModes();
    const { templateId } = Route.useParams();
    const search = useTemplateAiWizardSearch(templateId, Route.useSearch());
    const required_events = useMemo(() => search.required_events || [], [search.required_events]);
    const { steps, currentStep, markStepComplete, nextStep, updateEventSteps, setCurrentStep } =
        useTemplateNavigationStore();
    const navigate = useNavigate();
    const { finalize, isSubmitting, isTemplateOwnerLookupBlockingSubmit } = useFinalizeTemplateWizard(
        templateId,
        search as Partial<TemplateSettingsSchemaT>
    );
    const isLastStep = steps.findIndex((s) => s.key === "events") === steps.length - 1;

    const visibleEventOptions = useMemo(() => {
        const enabled = new Set(enabledEventTypes(accessModes));
        const events = EVENT_TYPES_WITH_SKIP.filter(
            (event) => event.value === skipInterventionOption.value || enabled.has(event.value as TrackingType)
        );
        return events;
    }, [accessModes]);

    const eventOptionLabel = (value: string) =>
        value === skipInterventionOption.value ? copy.noIntervention : copy.eventTypeLabel(value as TrackingType);

    // Set this page as the current step
    useEffect(() => {
        setCurrentStep("events"); // Events is the third step (index 2)
    }, [setCurrentStep]);

    useEffect(() => {
        if (required_events.length > 0) {
            markStepComplete("events");
            // Filter out "skip" for event steps (skip = no intervention)
            const effectiveEvents = required_events.filter((e) => e !== "skip");
            updateEventSteps(effectiveEvents);
        }
    }, [required_events, markStepComplete, updateEventSteps]);

    const handleEventSelect = (eventType: TrackingType | "skip") => {
        // Skip = store "skip" in URL for UI state, filter to [] when passing to next step
        const events = eventType === "skip" ? ["skip"] : [eventType];
        updateEventSteps(eventType === "skip" ? [] : [eventType]);
        const nextSettings = { ...search, required_events: events } as Partial<TemplateSettingsSchemaT>;
        const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
        if (!wizardSearchPayload) return;
        navigate({
            to: "/templates/$templateId/events",
            params: { templateId },
            search: wizardSearchPayload as z.infer<typeof eventsSearchSchema>,
            replace: true,
        });
    };

    const handleNext = () => {
        const currentIndex = steps.findIndex((s) => s.key === currentStep);
        const next = steps[currentIndex + 1];

        markStepComplete("events");

        // Add default clinician IDs based on event type if not already set
        // Filter out "skip" - pass empty array to next step when intervention skipped
        const effectiveEvents = required_events.filter((e) => e !== "skip");
        const searchUpdate: any = {
            ...search,
            required_events: effectiveEvents,
        };

        const eventType = effectiveEvents[0] as TrackingType | undefined;
        if (auth?.id && eventType) {
            // Helper to set clinician ID only if not already valid
            const setClinicianIdIfEmpty = (fieldName: string) => {
                const currentValue = search[fieldName as keyof typeof search];
                if (!currentValue || !isUuid(currentValue as string)) {
                    searchUpdate[fieldName] = auth.id;
                }
            };

            // Set referring_clinician_id for all event types except education
            if (eventType !== "education" && eventType !== "product") {
                setClinicianIdIfEmpty("referring_clinician_id");
            }

            // Set specific clinician field for each event type
            switch (eventType) {
                case "injection":
                    setClinicianIdIfEmpty("injecting_clinician_id");
                    break;
                case "shockwave":
                    setClinicianIdIfEmpty("shockwaving_clinician_id");
                    break;
                case "surgery":
                    setClinicianIdIfEmpty("operating_clinician_id");
                    break;
                case "education":
                    setClinicianIdIfEmpty("educated_by");
                    break;
                case "product":
                    setClinicianIdIfEmpty("provided_by");
                    break;
            }
        }

        if (next && next.route && typeof next.route.to === "string" && typeof next.route.params === "function") {
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", searchUpdate);
            if (!wizardSearchPayload) return;
            nextStep();
            navigate({
                to: next.route.to,
                params: next.route.params({ templateId }),
                search: wizardSearchPayload,
            });
        } else {
            // No intervention selected (skip) - this is now the last step
            finalize(searchUpdate);
        }
    };

    const handlePrevious = () => {
        const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", search);
        if (!wizardSearchPayload) return;
        navigate({
            to: "/templates/$templateId/trackers",
            params: { templateId },
            search: wizardSearchPayload as never,
        });
    };

    return (
        <div className="space-y-8">
            <LockableField field="required_events">
                <c.ui.Card>
                    <c.ui.CardHeader className="space-y-0">
                        <TemplateFieldLockLabelRow label={<c.ui.CardTitle>{copy.interventionStep}</c.ui.CardTitle>} />
                    </c.ui.CardHeader>
                    <FormFieldLockedRegion>
                        <c.ui.CardContent>
                            {/* The primitive renders its own <form>. Picking a choice updates the URL, so swallow its submit. */}
                            <Questionnaire onSubmit={stopSubmit} onReset={stopSubmit}>
                                <QuestionnaireItem name="required_events" aria-label={copy.interventionStep}>
                                    <QuestionnaireChoices>
                                        {visibleEventOptions.map((event) => (
                                            <QuestionnaireChoice
                                                key={event.value}
                                                value={event.value}
                                                checked={required_events[0] === event.value}
                                                onChange={() => handleEventSelect(event.value as TrackingType | "skip")}
                                                inputProps={{ "data-testid": `event-option-${event.value}` } as never}
                                            >
                                                {eventOptionLabel(event.value)}
                                            </QuestionnaireChoice>
                                        ))}
                                    </QuestionnaireChoices>
                                </QuestionnaireItem>
                            </Questionnaire>
                        </c.ui.CardContent>
                    </FormFieldLockedRegion>
                </c.ui.Card>
            </LockableField>

            <TemplateStepFooter
                onPrimaryClick={handleNext}
                onBack={handlePrevious}
                primaryAction={isLastStep ? "save" : "next"}
                primaryLabel={isLastStep ? copy.saveTemplate : undefined}
                isLoadingPrimary={isLastStep ? isSubmitting : undefined}
                disabledPrimary={isLastStep ? isTemplateOwnerLookupBlockingSubmit : undefined}
            />
        </div>
    );
};

// Use partial schema - allow "skip" in required_events for skip intervention option
const eventsSearchSchema = templateSettingsSchema.partial().extend({
    required_events: z.array(z.union([TrackingType, z.literal("skip")])).optional(),
});

export const Route = createFileRoute("/_app/templates/$templateId/events/")({
    component: EventsForm,
    validateSearch: (search) => eventsSearchSchema.parse(search),
});
